/**
 * useFitText — adapte la taille de fonte pour qu'un texte remplisse
 * au maximum son conteneur sans déborder.
 *
 * Algorithme : recherche binaire sur [minPx, maxPx].
 * À chaque étape, on applique la taille candidate directement sur le DOM
 * (sans re-render React) et on vérifie si le texte déborde.
 *
 * Le hook retourne une ref à attacher sur l'élément TEXT et une ref à
 * attacher sur le CONTENEUR. Il ne gère aucun état React — tout passe
 * par mutation directe du style, donc zéro re-render inutile.
 *
 * Un ResizeObserver relance le calcul quand les dimensions changent
 * (rotation tablette, redimensionnement fenêtre en dev).
 */
import { useRef, useLayoutEffect, useCallback } from "react";

export interface FitTextOptions {
  /** Taille minimale en px (défaut : 16). */
  minPx?: number;
  /** Taille maximale en px (défaut : 200). */
  maxPx?: number;
  /**
   * Contenu texte observé : quand il change, le calcul est relancé.
   * Passer la chaîne du message pour que le hook réagisse aux updates.
   */
  content: string;
}

/**
 * Recherche binaire pure : retourne la plus grande taille (px) telle que
 * le texte ne déborde pas du conteneur.
 *
 * Fonction extraite du hook pour être testable indépendamment.
 *
 * @param setSize  — callback qui applique la taille sur l'élément texte
 * @param overflows — callback qui retourne true si le texte déborde
 * @param min — borne inférieure en px
 * @param max — borne supérieure en px
 */
export function binarySearchFontSize(
  setSize: (px: number) => void,
  overflows: () => boolean,
  min: number,
  max: number,
): number {
  let lo = min;
  let hi = max;

  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    setSize(mid);
    if (overflows()) {
      hi = mid;
    } else {
      lo = mid;
    }
  }

  // Vérification finale : lo ne déborde pas, hi déborde (ou == max).
  setSize(lo);
  if (overflows()) {
    // lo déborde aussi → revenir à la borne inférieure
    const safe = Math.max(min, lo - 1);
    setSize(safe);
    return safe;
  }
  return lo;
}

export function useFitText(options: FitTextOptions) {
  const { minPx = 16, maxPx = 200, content } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);

  const fit = useCallback(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (!container || !text) return;

    const containerH = container.clientHeight;
    const containerW = container.clientWidth;
    if (containerH === 0 || containerW === 0) return;

    binarySearchFontSize(
      (px) => { text.style.fontSize = `${px}px`; },
      () => text.scrollHeight > containerH || text.scrollWidth > containerW,
      minPx,
      maxPx,
    );
  }, [minPx, maxPx]);

  // Relancer à chaque changement de contenu
  useLayoutEffect(() => {
    fit();
  }, [content, fit]);

  // Relancer sur resize (rotation tablette, redimensionnement fenêtre)
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const ro = new ResizeObserver(() => { fit(); });
    ro.observe(container);
    return () => ro.disconnect();
  }, [fit]);

  return { containerRef, textRef };
}
